import React, { useEffect, useState, useCallback, useRef } from 'react';
import { Link, useParams, useNavigate, useSearchParams } from 'react-router-dom';
import {
  ChevronRight,
  ChevronLeft,
  ArrowRight,
  Loader2,
  AlertCircle,
  Send,
  BookOpen,
  Lightbulb,
  Timer,
  Eye,
  EyeOff,
  AlertTriangle,
  FileText,
  HelpCircle,
  Boxes,
  ListChecks,
  GitBranch,
  Workflow,
  Code2,
  ClipboardList,
  Save,
} from 'lucide-react';
import { LLDPracticeProvider, useLLDPractice, WorkspaceState, SessionState } from '../../../context/LLDPracticeContext';
import { lldProblemService, LLDProblem } from '../../../services/lldProblemService';
import { lldProgressService, LLDProgressData } from '../../../services/lldProgressService';
import { lldEvaluationService, WorkspaceStateSubmission } from '../../../services/lldEvaluationService';
import { lldCustomProblemService, CustomLLDProblemInput } from '../../../services/lldCustomProblemService';
import { Mode, SectionId, SECTION_SEQUENCE } from '../../../utils/sectionStateMachine';
import { generateProblemPdf, ProblemPdfData, ProblemPdfTheme } from '../../../utils/lldPdfGenerator';

// Panel components
import EntityDiscoveryPanel from '../../../components/lld/EntityDiscoveryPanel';
import RequirementsPanel from '../../../components/lld/RequirementsPanel';
import ResponsibilityMapper from '../../../components/lld/ResponsibilityMapper';
import RelationshipDesigner from '../../../components/lld/RelationshipDesigner';
import UMLBuilder from '../../../components/lld/UMLBuilder';
import CodeEditorPanel from '../../../components/lld/CodeEditorPanel';
import TimerComponent from '../../../components/lld/TimerComponent';
import SectionNavigator from '../../../components/lld/SectionNavigator';
import SectionShell from '../../../components/lld/SectionShell';
import PracticeSummaryModal from '../../../components/lld/PracticeSummaryModal';

// ─── Section Labels ─────────────────────────────────────────────────────────────

const SECTION_LABELS: Record<SectionId, string> = {
  problem: 'Understand the Problem',
  requirements: 'Requirements',
  entities: 'Entity Discovery',
  responsibilities: 'Responsibility Mapping',
  relationships: 'Relationships',
  uml: 'UML Diagram',
  implementation: 'Implementation',
  testing: 'Testing',
};

// ─── Scratch / blank session ─────────────────────────────────────────────────────
// A reserved problem id used to open an empty workspace (no backend record).
// The user types their own problem statement on Step 1.
const SCRATCH_PROBLEM_ID = 'scratch';

/** Build an in-memory, blank problem shaped like LLDProblem (no fetch needed). */
const buildScratchProblem = (): LLDProblem =>
({
  id: SCRATCH_PROBLEM_ID,
  title: 'Untitled problem',
  description: '',
  difficulty: '',
  category: '',
  clarifications: [],
  expectedEntities: [],
  expectedRelationships: [],
  testCases: [],
  referenceSolution: null,
  hints: {},
} as unknown as LLDProblem);

// ─── Section step descriptions for Learn Mode ─────────────────────────────────────

const SECTION_DESCRIPTIONS: Record<SectionId, string> = {
  problem: 'Read and understand the problem statement, then resolve ambiguity with clarifying questions. Pin down the core requirements and constraints before designing.',
  requirements: 'Capture the functional requirements, non-functional requirements, and assumptions that scope the problem before you start designing.',
  entities: 'Identify the key domain entities (objects/classes) in the system. Think about the nouns in the problem statement — these often become your entities.',
  responsibilities: 'Assign attributes (data) and methods (behavior) to each entity. Follow the Single Responsibility Principle — each class should have one reason to change.',
  relationships: 'Define how entities relate to each other. Consider ownership (composition), shared references (aggregation), inheritance, and dependencies.',
  uml: 'Create a UML class diagram representing your design. This visual representation helps communicate your design clearly in an interview.',
  implementation: 'Write the actual code implementing your design. Focus on correct OOP structure, clean interfaces, and handling the core use cases.',
  testing: 'Review the functional scenarios and edge cases your implementation should satisfy, and self-check your design against each one.',
};

// ─── Per-section shell metadata (icon + concise purpose) ─────────────────────────

const SECTION_SHELL_META: Record<SectionId, { purpose: string; icon: React.ElementType }> = {
  problem: { purpose: 'Read the prompt, resolve ambiguity, and pin down the core requirements and constraints.', icon: FileText },
  requirements: { purpose: 'List the functional needs, non-functional constraints, and assumptions that scope your design.', icon: ClipboardList },
  entities: { purpose: 'Identify the domain objects — the nouns that become your classes.', icon: Boxes },
  responsibilities: { purpose: 'Give each entity its attributes (data) and methods (behavior).', icon: ListChecks },
  relationships: { purpose: 'Define how entities connect — composition, aggregation, inheritance.', icon: GitBranch },
  uml: { purpose: 'Lay out the class diagram to communicate your design at a glance.', icon: Workflow },
  implementation: { purpose: 'Turn the design into clean, working code.', icon: Code2 },
  testing: { purpose: 'Reserved for a later release.', icon: Code2 },
};

// ─── Reference Solution renderer ────────────────────────────────────────────────

const ReferenceSolutionView: React.FC<{
  referenceSolution: any;
  activeLanguage: string;
  tone: 'blue' | 'green';
}> = ({ referenceSolution, activeLanguage, tone }) => {
  if (!referenceSolution) return null;

  const explanation: string | undefined = referenceSolution.explanation;
  const codeByLang: Record<string, any[]> = referenceSolution.code || {};
  const files = codeByLang[activeLanguage] || codeByLang['typescript'] || [];

  const surface =
    tone === 'blue'
      ? 'bg-blue-100/70 dark:bg-blue-900/30 text-blue-900 dark:text-blue-100 border-blue-200 dark:border-blue-800'
      : 'bg-green-100/70 dark:bg-green-900/30 text-green-900 dark:text-green-100 border-green-200 dark:border-green-800';

  return (
    <div className={`mt-2 rounded-lg border p-3 space-y-3 ${surface}`}>
      {explanation && (
        <div>
          <div className="text-[11px] font-semibold uppercase tracking-wide opacity-70 mb-1">
            Approach
          </div>
          <p className="text-xs leading-relaxed whitespace-pre-wrap">{explanation}</p>
        </div>
      )}
      {files.length > 0 && (
        <div className="space-y-2">
          <div className="text-[11px] font-semibold uppercase tracking-wide opacity-70">
            Reference code ({activeLanguage})
          </div>
          {files.map((file: any, i: number) => {
            const name = file.path || file.fileName || file.name || `file-${i + 1}`;
            const content = file.content || file.code || '';
            return (
              <div key={i} className="rounded-md overflow-hidden border border-black/10 dark:border-white/10">
                <div className="px-3 py-1.5 bg-black/5 dark:bg-white/5 text-[11px] font-mono">{name}</div>
                <pre className="px-3 py-2 bg-gray-900 text-gray-100 text-[12px] font-mono overflow-x-auto whitespace-pre">
                  <code>{content}</code>
                </pre>
              </div>
            );
          })}
        </div>
      )}
      {!explanation && files.length === 0 && (
        <p className="text-xs opacity-70">No reference solution content is available for this problem yet.</p>
      )}
    </div>
  );
};

// ─── Inner Workspace (uses context) ─────────────────────────────────────────────

interface WorkspaceInnerProps {
  problem: LLDProblem;
  mode: Mode;
  savedProgress: LLDProgressData | null;
}

// ─── Interview timer persistence (browser-local, survives page refresh) ──────
// The remaining time is ephemeral session state, so we persist it to
// localStorage (updated every tick) rather than the DB. On refresh it is
// restored in a PAUSED state with the saved remaining time.
const timerStorageKey = (problemId: string, mode: string) => `lld-timer-${problemId}-${mode}`;
const workspaceDraftKey = (problemId: string, mode: string) => `lld-workspace-draft-v1-${problemId}-${mode}`;

interface LocalWorkspaceDraft {
  savedAt: number;
  workspaceState: WorkspaceState;
  sessionState: Pick<SessionState,
    'mode' | 'currentSection' | 'sectionProgress' | 'visitHistory' |
    'timeSpentPerSection' | 'timer' | 'hintsRevealed' | 'solutionsRevealed' |
    'layoutPreset' | 'panelVisibility' | 'panelSizes'>;
}

function readWorkspaceDraft(problemId: string, mode: string): LocalWorkspaceDraft | null {
  try {
    const raw = localStorage.getItem(workspaceDraftKey(problemId, mode));
    if (!raw) return null;
    const draft = JSON.parse(raw) as LocalWorkspaceDraft;
    return draft?.workspaceState && draft?.sessionState ? draft : null;
  } catch {
    return null;
  }
}

function readSavedTimer(
  problemId: string,
  mode: string
): { totalSeconds: number; remainingSeconds: number } | null {
  try {
    const raw = localStorage.getItem(timerStorageKey(problemId, mode));
    if (!raw) return null;
    const t = JSON.parse(raw);
    if (typeof t?.totalSeconds === 'number' && typeof t?.remainingSeconds === 'number') {
      return { totalSeconds: t.totalSeconds, remainingSeconds: t.remainingSeconds };
    }
    return null;
  } catch {
    return null;
  }
}

const WorkspaceInner: React.FC<WorkspaceInnerProps> = ({ problem, mode, savedProgress }) => {
  const { state, dispatch } = useLLDPractice();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [submitting, setSubmitting] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [revealClarifications, setRevealClarifications] = useState(false);
  const [initialized, setInitialized] = useState(false);

  // Interview mode expiry state
  const [showExpiryModal, setShowExpiryModal] = useState(false);
  const expiryTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const hasTriggeredExpiry = useRef(false);

  // Practice mode hint tracking
  const [showSolution, setShowSolution] = useState(false);

  // ─── Scratch / blank session state ──────────────────────────────────────────────
  const isScratch = problem.id === SCRATCH_PROBLEM_ID;
  const briefTitleInputRef = useRef<HTMLInputElement | null>(null);
  const [savingCustom, setSavingCustom] = useState(false);
  const [briefError, setBriefError] = useState<string | null>(null);
  const brief = state.workspaceState.brief;

  // Initialize workspace state on mount
  useEffect(() => {
    if (initialized) return;

    // Set problem data
    dispatch({
      type: 'SET_PROBLEM',
      payload: {
        problemId: problem.id,
        title: problem.title,
        description: problem.description,
        hints: problem.hints || {},
        referenceSolution: problem.referenceSolution || null,
      },
    });

    // Set mode. The interview timer starts PAUSED and persists across refresh
    // via localStorage, so we seed it with any previously saved remaining time.
    const minutesParam = parseInt(searchParams.get('minutes') || '', 10);
    const interviewSeconds =
      mode === 'interview' && Number.isFinite(minutesParam) && minutesParam > 0
        ? minutesParam * 60
        : undefined;
    const savedTimer = mode === 'interview' ? readSavedTimer(problem.id, mode) : null;
    const timerTotal = savedTimer?.totalSeconds || interviewSeconds || 2700;
    const timerRemaining =
      typeof savedTimer?.remainingSeconds === 'number'
        ? Math.min(savedTimer.remainingSeconds, timerTotal)
        : timerTotal;
    dispatch({
      type: 'SET_MODE',
      payload: { mode, interviewSeconds: timerTotal, interviewRemainingSeconds: timerRemaining },
    });

    // Restore server progress first. A newer browser draft is applied below so
    // even a refresh before the debounced API save cannot erase recent work.
    if (savedProgress) {
      dispatch({
        type: 'RESTORE_STATE',
        payload: {
          workspaceState: {
            entities: (savedProgress.entities || []) as any,
            responsibilities: (savedProgress.responsibilities || {}) as any,
            relationships: (savedProgress.relationships || []) as any,
            umlDiagram: (savedProgress.umlDiagram || { elements: [], connections: [] }) as any,
            codeFiles: {
              typescript: savedProgress.codeFiles?.typescript || [],
              javascript: savedProgress.codeFiles?.javascript || [],
              python: savedProgress.codeFiles?.python || [],
            } as any,
            activeLanguage: (['typescript', 'javascript', 'python'].includes(savedProgress.activeLanguage || '')
              ? savedProgress.activeLanguage
              : 'typescript') as any,
            testResults: null,
            requirements: ((savedProgress as any).requirements || []) as any,
            brief: ((savedProgress as any).brief || { title: '', description: '' }) as any,
          },
          sessionState: {
            mode,
            visitHistory: (savedProgress.sectionVisitHistory || []) as any,
            timeSpentPerSection: (savedProgress.timeSpentPerSection || {}) as any,
            timer: mode === 'interview'
              ? { totalSeconds: timerTotal, remainingSeconds: timerRemaining, isRunning: false }
              : null,
            hintsRevealed: savedProgress.hintsRevealed || {},
            solutionsRevealed: savedProgress.solutionsRevealed || [],
            layoutPreset: (savedProgress.layoutPreset || 'balanced') as any,
            panelVisibility: savedProgress.panelVisibility || {},
            panelSizes: savedProgress.panelSizes || {},
          } as any,
        },
      });
    }

    const localDraft = readWorkspaceDraft(problem.id, mode);
    const serverSavedAt = savedProgress?.lastSavedAt || savedProgress?.updatedAt;
    const serverSavedTime = serverSavedAt ? new Date(serverSavedAt).getTime() : 0;
    if (localDraft && localDraft.savedAt >= serverSavedTime) {
      dispatch({
        type: 'RESTORE_STATE',
        payload: {
          workspaceState: localDraft.workspaceState,
          sessionState: {
            ...localDraft.sessionState,
            timer: localDraft.sessionState.timer
              ? { ...localDraft.sessionState.timer, isRunning: false }
              : null,
          } as SessionState,
        },
      });
    }

    setInitialized(true);
  }, [initialized, problem, mode, savedProgress, dispatch]);

  // Immediate local draft persistence complements the debounced server save.
  // This is intentionally synchronous so refresh/closing the tab cannot drop
  // the last few seconds of work.
  useEffect(() => {
    if (!initialized || state.problemState.problemId !== problem.id) return;
    const session = state.sessionState;
    const draft: LocalWorkspaceDraft = {
      savedAt: Date.now(),
      workspaceState: state.workspaceState,
      sessionState: {
        mode: session.mode,
        currentSection: session.currentSection,
        sectionProgress: session.sectionProgress,
        visitHistory: session.visitHistory,
        timeSpentPerSection: session.timeSpentPerSection,
        timer: session.timer ? { ...session.timer, isRunning: false } : null,
        hintsRevealed: session.hintsRevealed,
        solutionsRevealed: session.solutionsRevealed,
        layoutPreset: session.layoutPreset,
        panelVisibility: session.panelVisibility,
        panelSizes: session.panelSizes,
      },
    };
    try {
      localStorage.setItem(workspaceDraftKey(problem.id, mode), JSON.stringify(draft));
    } catch {
      // Browser storage may be disabled or full; API auto-save remains active.
    }
  }, [initialized, problem.id, mode, state.problemState.problemId, state.workspaceState, state.sessionState]);

  // Persist the interview timer to localStorage on every tick / change, so a
  // page refresh restores it (paused) with the saved remaining time.
  useEffect(() => {
    if (mode !== 'interview' || !problem?.id) return;
    const t = state.sessionState.timer;
    if (!t) return;
    try {
      localStorage.setItem(
        timerStorageKey(problem.id, mode),
        JSON.stringify({ totalSeconds: t.totalSeconds, remainingSeconds: t.remainingSeconds })
      );
    } catch {
      /* ignore quota / disabled storage */
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, problem?.id, state.sessionState.timer?.totalSeconds, state.sessionState.timer?.remainingSeconds]);

  // ─── Interview Mode: Timer expiry logic (Task 10.1) ─────────────────────────────
  useEffect(() => {
    if (mode !== 'interview' || !state.sessionState.timer) return;

    const { remainingSeconds } = state.sessionState.timer;

    // When timer reaches 0, show submission prompt
    if (remainingSeconds <= 0 && !hasTriggeredExpiry.current) {
      hasTriggeredExpiry.current = true;
      setShowExpiryModal(true);

      // Auto-submit after 60 seconds if user doesn't act
      expiryTimeoutRef.current = setTimeout(() => {
        handleSubmit();
      }, 60000);
    }

    return () => {
      if (expiryTimeoutRef.current) {
        clearTimeout(expiryTimeoutRef.current);
      }
    };
  }, [mode, state.sessionState.timer?.remainingSeconds]);

  // ─── Persist state on navigation away / browser close (Task 10.1) ───────────────
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      // Trigger a save before the page unloads
      if (state.problemState.problemId) {
        const data = buildSaveData();
        // Use sendBeacon for reliable delivery on unload
        const blob = new Blob([JSON.stringify(data)], { type: 'application/json' });
        navigator.sendBeacon(
          `/api/v1/lld-practice/progress/${state.problemState.problemId}`,
          blob
        );
      }
      e.preventDefault();
      e.returnValue = '';
    };

    if (mode === 'interview') {
      window.addEventListener('beforeunload', handleBeforeUnload);
    }

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [mode, state]);

  // ─── Build save data helper ─────────────────────────────────────────────────────
  const buildSaveData = useCallback(() => {
    return {
      entities: state.workspaceState.entities,
      responsibilities: state.workspaceState.responsibilities,
      relationships: state.workspaceState.relationships,
      umlDiagram: state.workspaceState.umlDiagram,
      codeFiles: state.workspaceState.codeFiles,
      activeLanguage: state.workspaceState.activeLanguage,
      timeSpentPerSection: state.sessionState.timeSpentPerSection,
      timerTotalSeconds: state.sessionState.timer?.totalSeconds,
      timerRemainingSeconds: state.sessionState.timer?.remainingSeconds,
      hintsRevealed: state.sessionState.hintsRevealed,
      solutionsRevealed: state.sessionState.solutionsRevealed,
      layoutPreset: state.sessionState.layoutPreset,
      panelVisibility: state.sessionState.panelVisibility,
      panelSizes: state.sessionState.panelSizes,
    };
  }, [state]);

  // Handle submission
  const handleSubmit = useCallback(async () => {
    if (submitting) return;
    setSubmitting(true);

    // Clear expiry timeout if active
    if (expiryTimeoutRef.current) {
      clearTimeout(expiryTimeoutRef.current);
      expiryTimeoutRef.current = null;
    }

    try {
      const submission: WorkspaceStateSubmission = {
        entities: state.workspaceState.entities,
        responsibilities: state.workspaceState.responsibilities,
        relationships: state.workspaceState.relationships,
        umlDiagram: state.workspaceState.umlDiagram,
        codeFiles: state.workspaceState.codeFiles,
        activeLanguage: state.workspaceState.activeLanguage,
        mode,
        totalTimeSpent: state.sessionState.timeSpentPerSection
          ? Object.values(state.sessionState.timeSpentPerSection).reduce((a, b) => a + b, 0)
          : 0,
        timeSpentPerSection: state.sessionState.timeSpentPerSection,
      };

      await lldEvaluationService.submitEvaluation(problem.id, submission);
      navigate(`/practice/lld/${problem.id}/evaluation`);
    } catch (err) {
      console.error('Submission failed:', err);
      setSubmitting(false);
    }
  }, [submitting, state, mode, problem.id, navigate]);

  // Handle expiry modal "Submit Now"
  const handleExpirySubmit = useCallback(() => {
    setShowExpiryModal(false);
    handleSubmit();
  }, [handleSubmit]);

  // Handle expiry modal dismiss (continue working — auto-submit will still fire)
  const handleExpiryContinue = useCallback(() => {
    setShowExpiryModal(false);
    // Auto-submit timeout is still running
  }, []);

  // ─── Practice Mode: Hint reveal handler ─────────────────────────────────────────
  const handleRevealHint = useCallback(() => {
    const currentSection = state.sessionState.currentSection;
    dispatch({ type: 'REVEAL_HINT', payload: currentSection });
  }, [state.sessionState.currentSection, dispatch]);

  // ─── Practice Mode: Solution reveal handler ─────────────────────────────────────
  const handleRevealSolution = useCallback(() => {
    const currentSection = state.sessionState.currentSection;
    dispatch({ type: 'REVEAL_SOLUTION', payload: currentSection });
    setShowSolution(true);
  }, [state.sessionState.currentSection, dispatch]);

  // ─── Download a comprehensive PDF of the whole practice ─────────────────────────
  const buildPdfData = useCallback((): ProblemPdfData => {
    const ws = state.workspaceState;
    return {
      title: isScratch ? (ws.brief.title || 'Untitled problem') : problem.title,
      description: isScratch ? ws.brief.description : problem.description,
      difficulty: problem.difficulty,
      category: problem.category,
      mode,
      clarifications: problem.clarifications || [],
      requirements: ws.requirements ?? [],
      entities: ws.entities,
      responsibilities: ws.responsibilities as any,
      relationships: ws.relationships as any,
      uml: ws.umlDiagram as any,
      codeFiles: ws.codeFiles[ws.activeLanguage] || [],
      activeLanguage: ws.activeLanguage,
      testCases: (problem.testCases as any) || [],
    };
  }, [state.workspaceState, problem, mode, isScratch]);

  const handleDownloadPdf = useCallback((theme: ProblemPdfTheme) => {
    generateProblemPdf(buildPdfData(), theme);
  }, [buildPdfData]);

  // ─── Save a scratch session as a reusable custom problem ────────────────────────
  const handleSaveAsCustom = useCallback(async () => {
    if (savingCustom) return;

    const titleText = brief.title.trim();
    if (!titleText) {
      // Guard: title is required. Surface inline validation and focus Step 1 input.
      setBriefError('Name your problem before saving.');
      dispatch({ type: 'SET_SECTION', payload: 'problem' });
      setTimeout(() => briefTitleInputRef.current?.focus(), 0);
      return;
    }

    setBriefError(null);
    setSavingCustom(true);

    try {
      const ws = state.workspaceState;
      const entityNameById = new Map(ws.entities.map((e) => [e.id, e.name]));

      const expectedEntities = ws.entities.map((e) => {
        const resp = ws.responsibilities[e.id];
        return {
          name: e.name,
          description: e.description || '',
          attributes: resp ? resp.attributes.map((a) => ({ name: a.name, type: a.type })) : [],
          methods: resp
            ? resp.methods.map((m) => ({ name: m.name, parameters: m.parameters, returnType: m.returnType }))
            : [],
        };
      });

      const expectedRelationships = ws.relationships
        .map((r) => ({
          source: entityNameById.get(r.sourceEntityId) || '',
          target: entityNameById.get(r.targetEntityId) || '',
          type: r.type,
          cardinality: r.cardinality,
        }))
        .filter((r) => r.source && r.target);

      const input: CustomLLDProblemInput = {
        title: titleText.slice(0, 200),
        description: (brief.description || '').slice(0, 5000),
        expectedEntities,
        expectedRelationships,
        testCases: [],
      };

      const created = await lldCustomProblemService.create(input);
      await lldProgressService.saveProgress(created.id, {
        mode,
        ...buildSaveData(),
        requirements: ws.requirements,
        brief: ws.brief,
        sectionStatus: Object.fromEntries(Object.entries(state.sessionState.sectionStates)),
        sectionVisitHistory: state.sessionState.visitHistory.map((visit) => ({
          section: visit.section,
          timestamp: visit.timestamp,
        })),
      });
      navigate(`/practice/lld/${created.id}/mode`);
    } catch (err: any) {
      console.error('Save as custom failed:', err);
      setBriefError(err?.message || 'Could not save your problem. Please try again.');
      setSavingCustom(false);
    }
  }, [savingCustom, brief, state.workspaceState, state.sessionState, mode, buildSaveData, dispatch, navigate]);

  const currentSection = state.sessionState.currentSection;
  const sectionStates = state.sessionState.sectionStates;

  // Get hints for current section
  const currentHints = state.problemState.hints[currentSection] || [];
  const hintsRevealedCount = state.sessionState.hintsRevealed[currentSection] || 0;
  const maxHintsPerSection = 3;
  const canRevealMoreHints = hintsRevealedCount < Math.min(maxHintsPerSection, currentHints.length);

  // Check if solution is revealed for current section
  const isSolutionRevealed = state.sessionState.solutionsRevealed.includes(currentSection);

  // Check if current section is completed (for Learn Mode reference solution gating)
  const isSectionCompleted = sectionStates[currentSection] === 'completed';

  // Custom problems carry no author-only content (reference solution / hints).
  // The id namespace (CLLD@) is the single source of truth for this.
  // Scratch/blank sessions degrade the same way (no reference solution, no hints).
  const isCustom = problem.id.startsWith('CLLD@') || isScratch;

  // ─── Section navigation helpers (footer action bar) ─────────────────────────────
  const currentIndex = SECTION_SEQUENCE.indexOf(currentSection);
  const nextSection: SectionId | null =
    currentIndex >= 0 && currentIndex < SECTION_SEQUENCE.length - 1
      ? SECTION_SEQUENCE[currentIndex + 1]
      : null;
  const prevSection: SectionId | null =
    currentIndex > 0 ? SECTION_SEQUENCE[currentIndex - 1] : null;

  const goToSection = useCallback(
    (s: SectionId) => dispatch({ type: 'SET_SECTION', payload: s }),
    [dispatch]
  );

  const handleCompleteAndContinue = useCallback(() => {
    dispatch({ type: 'COMPLETE_SECTION', payload: currentSection });
    if (nextSection) {
      dispatch({ type: 'SET_SECTION', payload: nextSection });
    } else {
      handleSubmit();
    }
  }, [dispatch, currentSection, nextSection, handleSubmit]);

  // ─── Render section content based on current section ────────────────────────────

  const renderSectionContent = () => {
    switch (currentSection) {
      case 'problem': {
        const d = (problem.difficulty || '').toLowerCase();
        const diff = d.includes('begin')
          ? { dot: 'bg-emerald-500', cls: 'bg-emerald-50 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300' }
          : d.includes('inter')
            ? { dot: 'bg-amber-500', cls: 'bg-amber-50 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300' }
            : d.includes('adv')
              ? { dot: 'bg-rose-500', cls: 'bg-rose-50 text-rose-700 dark:bg-rose-900/30 dark:text-rose-300' }
              : { dot: 'bg-gray-400', cls: 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-300' };
        return (
          <SectionShell step={currentIndex + 1} total={SECTION_SEQUENCE.length} title={SECTION_LABELS.problem} purpose={SECTION_SHELL_META.problem.purpose} icon={SECTION_SHELL_META.problem.icon} reading>
            <div className="space-y-5">
              {/* Brief card */}
              {isScratch ? (
                <div className="rounded-2xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-6 sm:p-7">
                  <span className="text-[11px] font-semibold uppercase tracking-[0.14em] text-primary-600/80 dark:text-primary-400/80">
                    Your brief
                  </span>
                  <p className="mt-1 text-sm text-gray-600 dark:text-gray-400 leading-relaxed">
                    Type the problem you were given. This is your blank canvas — fill in every step as you go.
                  </p>

                  {/* Title input */}
                  <div className="mt-5">
                    <label htmlFor="scratch-title" className="block text-[11px] font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-500 mb-1.5">
                      Title
                    </label>
                    <input
                      id="scratch-title"
                      ref={briefTitleInputRef}
                      type="text"
                      value={brief.title}
                      maxLength={200}
                      placeholder="Name your problem"
                      onChange={(e) => {
                        if (briefError) setBriefError(null);
                        dispatch({ type: 'SET_BRIEF', payload: { title: e.target.value } });
                      }}
                      className="w-full text-lg font-semibold text-gray-900 dark:text-white bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500/40 focus:border-primary-400 placeholder:text-gray-400 dark:placeholder:text-gray-500"
                    />
                    <div className="mt-1 flex items-center justify-between">
                      {briefError ? (
                        <span className="text-xs font-medium text-rose-600 dark:text-rose-400">{briefError}</span>
                      ) : (
                        <span />
                      )}
                      <span className="text-[11px] text-gray-500 dark:text-gray-500 tabular-nums">{brief.title.length}/200</span>
                    </div>
                  </div>

                  {/* Description textarea */}
                  <div className="mt-5 pt-5 border-t border-gray-100 dark:border-gray-800">
                    <label htmlFor="scratch-description" className="block text-[11px] font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-500 mb-1.5">
                      Problem statement
                    </label>
                    <textarea
                      id="scratch-description"
                      value={brief.description}
                      maxLength={5000}
                      rows={8}
                      placeholder="Write the problem statement you were given…"
                      onChange={(e) => dispatch({ type: 'SET_BRIEF', payload: { description: e.target.value } })}
                      className="w-full text-sm text-gray-700 dark:text-gray-300 leading-7 bg-gray-50 dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 focus:outline-none focus:ring-2 focus:ring-primary-500/40 focus:border-primary-400 px-3 py-2 resize-y placeholder:text-gray-400 dark:placeholder:text-gray-500"
                    />
                    <div className="mt-1 flex items-center justify-end">
                      <span className="text-[11px] text-gray-500 dark:text-gray-500 tabular-nums">{brief.description.length}/5000</span>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="rounded-2xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-6 sm:p-7">
                  <span className="text-[11px] font-semibold uppercase tracking-[0.14em] text-primary-600/80 dark:text-primary-400/80">
                    The brief
                  </span>
                  <h3 className="mt-1.5 text-2xl font-bold text-gray-900 dark:text-white tracking-tight">
                    {problem.title}
                  </h3>
                  <div className="mt-3 flex flex-wrap items-center gap-2">
                    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${diff.cls}`}>
                      <span className={`w-1.5 h-1.5 rounded-full ${diff.dot}`} />
                      {problem.difficulty}
                    </span>
                    <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-300">
                      <BookOpen className="w-3 h-3" />
                      {problem.category}
                    </span>
                  </div>
                  <div className="mt-5 pt-5 border-t border-gray-100 dark:border-gray-800">
                    <p className="text-[15px] text-gray-700 dark:text-gray-300 whitespace-pre-wrap leading-7">
                      {problem.description}
                    </p>
                  </div>
                </div>
              )}

              {/* Clarify the scope — think first, reveal on demand */}
              <div className="rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-5">
                <div className="flex items-start gap-3">
                  <span className="w-9 h-9 rounded-xl bg-primary-50 dark:bg-primary-900/30 text-primary-600 dark:text-primary-300 flex items-center justify-center flex-shrink-0">
                    <HelpCircle className="w-5 h-5" />
                  </span>
                  <div className="min-w-0">
                    <h3 className="text-sm font-bold text-gray-900 dark:text-white">Clarify the scope first</h3>
                    <p className="text-sm text-gray-600 dark:text-gray-400 mt-0.5 leading-relaxed">
                      The prompt is intentionally vague. Decide what you&apos;d ask an interviewer and capture your assumptions in the Requirements step — then compare with how a strong candidate scoped it.
                    </p>
                  </div>
                </div>

                {/* Angles worth probing — lightweight prompts (hover for detail) */}
                <div className="mt-4">
                  <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-500 mb-2">Angles worth probing</p>
                  <div className="flex flex-wrap gap-1.5">
                    {[
                      { t: 'Scope', d: 'Which features are in vs out of scope?' },
                      { t: 'Scale', d: 'How many users, items, or requests?' },
                      { t: 'Constraints', d: 'Concurrency, performance, or platform limits?' },
                      { t: 'Edge cases', d: 'Invalid input, full capacity, failures?' },
                      { t: 'Non-functional', d: 'Persistence, security, extensibility needs?' },
                      { t: 'Assumptions', d: 'What can you safely assume and state?' },
                    ].map((a) => (
                      <span
                        key={a.t}
                        title={a.d}
                        className="px-2.5 py-1 rounded-full text-xs font-medium bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-300 cursor-default"
                      >
                        {a.t}
                      </span>
                    ))}
                  </div>
                </div>

                {/* Reveal sample clarifications */}
                {problem.clarifications && problem.clarifications.length > 0 ? (
                  <div className="mt-4 pt-4 border-t border-gray-100 dark:border-gray-800">
                    <button
                      onClick={() => setRevealClarifications((v) => !v)}
                      className="inline-flex items-center gap-2 text-sm font-medium text-primary-600 dark:text-primary-400 hover:text-primary-800 dark:hover:text-primary-200 transition-colors"
                    >
                      {revealClarifications ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      {revealClarifications ? 'Hide sample clarifications' : `Reveal sample clarifications (${problem.clarifications.length})`}
                    </button>
                    {revealClarifications && (
                      <div className="mt-3 space-y-3">
                        {problem.clarifications.map((c, i) => (
                          <div key={i} className="rounded-xl border border-gray-200 dark:border-gray-800 overflow-hidden">
                            <div className="flex items-start gap-2.5 px-4 py-3 bg-gray-50 dark:bg-gray-800/60 border-b border-gray-100 dark:border-gray-800">
                              <span className="mt-0.5 w-5 h-5 rounded-md bg-primary-100 dark:bg-primary-900/40 text-primary-600 dark:text-primary-300 text-[11px] font-bold flex items-center justify-center flex-shrink-0 tabular-nums">{i + 1}</span>
                              <p className="text-sm font-medium text-gray-900 dark:text-gray-100">{c.question}</p>
                            </div>
                            <div className="flex items-start gap-2.5 px-4 py-3">
                              <span className="mt-0.5 w-5 h-5 rounded-md bg-emerald-100 dark:bg-emerald-900/40 text-emerald-600 dark:text-emerald-300 text-[11px] font-bold flex items-center justify-center flex-shrink-0">A</span>
                              <p className="text-sm text-gray-600 dark:text-gray-400 leading-relaxed">{c.answer}</p>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="mt-4 pt-4 border-t border-gray-100 dark:border-gray-800">
                    <p className="text-sm text-gray-500 dark:text-gray-500 italic">
                      No sample clarifications for this problem — decide the scope yourself and capture it in Requirements.
                    </p>
                  </div>
                )}
              </div>
            </div>
          </SectionShell>
        );
      }

      case 'requirements':
        return (
          <SectionShell step={currentIndex + 1} total={SECTION_SEQUENCE.length} title={SECTION_LABELS.requirements} purpose={SECTION_SHELL_META.requirements.purpose} icon={SECTION_SHELL_META.requirements.icon}>
            <RequirementsPanel />
          </SectionShell>
        );

      case 'entities':
        return (
          <SectionShell step={currentIndex + 1} total={SECTION_SEQUENCE.length} title={SECTION_LABELS.entities} purpose={SECTION_SHELL_META.entities.purpose} icon={SECTION_SHELL_META.entities.icon}>
            <EntityDiscoveryPanel
              expectedEntities={problem.expectedEntities}
              onNavigateToResponsibilities={() => dispatch({ type: 'SET_SECTION', payload: 'responsibilities' })}
            />
          </SectionShell>
        );

      case 'responsibilities':
        return (
          <SectionShell step={currentIndex + 1} total={SECTION_SEQUENCE.length} title={SECTION_LABELS.responsibilities} purpose={SECTION_SHELL_META.responsibilities.purpose} icon={SECTION_SHELL_META.responsibilities.icon}>
            <ResponsibilityMapper
              suggestedResponsibilities={buildSuggestedResponsibilities()}
            />
          </SectionShell>
        );

      case 'relationships':
        return (
          <SectionShell step={currentIndex + 1} total={SECTION_SEQUENCE.length} title={SECTION_LABELS.relationships} purpose={SECTION_SHELL_META.relationships.purpose} icon={SECTION_SHELL_META.relationships.icon}>
            <RelationshipDesigner
              expectedRelationships={problem.expectedRelationships}
            />
          </SectionShell>
        );

      case 'uml':
        return (
          <SectionShell step={currentIndex + 1} total={SECTION_SEQUENCE.length} title={SECTION_LABELS.uml} purpose={SECTION_SHELL_META.uml.purpose} icon={SECTION_SHELL_META.uml.icon}>
            <UMLBuilder />
          </SectionShell>
        );

      case 'implementation':
        return (
          <SectionShell step={currentIndex + 1} total={SECTION_SEQUENCE.length} title={SECTION_LABELS.implementation} purpose={SECTION_SHELL_META.implementation.purpose} icon={SECTION_SHELL_META.implementation.icon}>
            <CodeEditorPanel />
          </SectionShell>
        );

      default:
        return null;
    }
  };

  // Build suggested responsibilities from problem's expected entities
  const buildSuggestedResponsibilities = () => {
    if (!problem.expectedEntities) return undefined;
    const suggestions: Record<string, { attributes: { name: string; type: string }[]; methods: { name: string; parameters: { name: string; type: string }[]; returnType: string }[] }> = {};
    problem.expectedEntities.forEach((entity) => {
      suggestions[entity.name] = {
        attributes: (entity.attributes || []) as { name: string; type: string }[],
        methods: (entity.methods || []) as { name: string; parameters: { name: string; type: string }[]; returnType: string }[],
      };
    });
    return suggestions;
  };

  // ─── Guidance rail content (Learn / Practice) ──────────────────────────────────

  const renderGuidanceRail = () => {
    // Learn Mode: step guidance + hints + reference solution
    if (mode === 'learn') {
      return (
        <>
          <div className="px-5 py-4 border-b border-gray-100 dark:border-gray-800">
            <div className="flex items-center gap-2 text-primary-600 dark:text-primary-400 mb-2">
              <BookOpen className="w-4 h-4" />
              <h3 className="text-sm font-semibold">Guidance</h3>
            </div>
            <p className="text-sm text-gray-600 dark:text-gray-400 leading-relaxed">
              {SECTION_DESCRIPTIONS[currentSection]}
            </p>
          </div>

          {isCustom ? (
            <div className="px-5 py-4 border-b border-gray-100 dark:border-gray-800">
              <h4 className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-500 mb-2.5">Hints</h4>
              <p className="text-sm text-gray-500 dark:text-gray-500 italic leading-relaxed">
                Hints aren&apos;t available for custom problems.
              </p>
            </div>
          ) : currentHints.length > 0 && (
            <div className="px-5 py-4 border-b border-gray-100 dark:border-gray-800">
              <h4 className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-500 mb-2.5">Hints</h4>
              <ul className="space-y-2.5">
                {currentHints.map((hint, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm text-gray-600 dark:text-gray-400">
                    <Lightbulb className="w-4 h-4 text-amber-500 mt-0.5 flex-shrink-0" />
                    <span className="leading-relaxed">{hint}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {isCustom ? (
            <div className="px-5 py-4">
              <h4 className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-500 mb-2.5">Reference solution</h4>
              <p className="text-sm text-gray-500 dark:text-gray-500 italic leading-relaxed">
                Reference solution isn&apos;t available for custom problems.
              </p>
            </div>
          ) : isSectionCompleted && state.problemState.referenceSolution && (
            <div className="px-5 py-4">
              <button
                onClick={() => setShowSolution(!showSolution)}
                className="inline-flex items-center gap-1.5 text-sm font-medium text-primary-600 dark:text-primary-400 hover:text-primary-800 dark:hover:text-primary-200"
              >
                {showSolution ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                {showSolution ? 'Hide reference solution' : 'View reference solution'}
              </button>
              {showSolution && (
                <ReferenceSolutionView
                  referenceSolution={state.problemState.referenceSolution}
                  activeLanguage={state.workspaceState.activeLanguage}
                  tone="blue"
                />
              )}
            </div>
          )}
        </>
      );
    }

    // Practice Mode: on-demand hints + solution
    if (mode === 'practice') {
      return (
        <>
          <div className="px-5 py-4 border-b border-gray-100 dark:border-gray-800">
            <h3 className="text-sm font-semibold text-gray-900 dark:text-white">Stuck?</h3>
            <p className="mt-1 text-xs text-gray-600 dark:text-gray-400 leading-relaxed">
              Reveal help only when you need it — it&apos;s tracked in your session.
            </p>
            <div className="mt-3 space-y-2">
              <button
                onClick={handleRevealHint}
                disabled={!canRevealMoreHints || isCustom}
                className={`w-full inline-flex items-center justify-center gap-1.5 px-3 py-2 text-sm font-medium rounded-lg border transition-colors ${canRevealMoreHints && !isCustom
                  ? 'bg-primary-50 dark:bg-primary-900/30 border-primary-200 dark:border-primary-800 text-primary-700 dark:text-primary-300 hover:bg-primary-100 dark:hover:bg-primary-900/50'
                  : 'bg-gray-50 dark:bg-gray-800 border-gray-200 dark:border-gray-700 text-gray-500 dark:text-gray-500 cursor-not-allowed'
                  }`}
              >
                <Lightbulb className="w-4 h-4" />
                {isCustom
                  ? 'Not available for custom problems'
                  : canRevealMoreHints
                    ? `Show hint (${hintsRevealedCount}/${Math.min(maxHintsPerSection, currentHints.length)})`
                    : 'No further hints'}
              </button>
              <button
                onClick={handleRevealSolution}
                disabled={isSolutionRevealed || isCustom}
                className={`w-full inline-flex items-center justify-center gap-1.5 px-3 py-2 text-sm font-medium rounded-lg border transition-colors ${!isSolutionRevealed && !isCustom
                  ? 'bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-300 hover:border-gray-300 dark:hover:border-gray-600'
                  : 'bg-gray-50 dark:bg-gray-800 border-gray-200 dark:border-gray-700 text-gray-500 dark:text-gray-500 cursor-not-allowed'
                  }`}
              >
                <Eye className="w-4 h-4" />
                {isCustom
                  ? 'Not available for custom problems'
                  : isSolutionRevealed
                    ? 'Solution revealed'
                    : 'Show solution'}
              </button>
            </div>
          </div>

          {hintsRevealedCount > 0 && (
            <div className="px-5 py-4 border-b border-gray-100 dark:border-gray-800">
              <h4 className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-500 mb-2.5">Revealed hints</h4>
              <ul className="space-y-2.5">
                {currentHints.slice(0, hintsRevealedCount).map((hint, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm text-gray-600 dark:text-gray-400">
                    <Lightbulb className="w-4 h-4 text-amber-500 mt-0.5 flex-shrink-0" />
                    <span className="leading-relaxed"><span className="font-medium text-gray-700 dark:text-gray-300">Hint {i + 1}:</span> {hint}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {isSolutionRevealed && state.problemState.referenceSolution && (
            <div className="px-5 py-4">
              <ReferenceSolutionView
                referenceSolution={state.problemState.referenceSolution}
                activeLanguage={state.workspaceState.activeLanguage}
                tone="green"
              />
            </div>
          )}
        </>
      );
    }

    return null;
  };

  return (
    <div className="lld-page lld-workspace-page flex flex-col h-[calc(100vh-4rem)] mt-16">
      {/* Practice summary preview modal */}
      {showPreview && (
        <PracticeSummaryModal
          data={buildPdfData()}
          onClose={() => setShowPreview(false)}
          onDownload={handleDownloadPdf}
        />
      )}

      {/* Top Bar: Breadcrumb + Mode indicator + Submit */}
      <div className="lld-workspace-toolbar flex items-center justify-between px-4 py-3 bg-white dark:bg-gray-900 border-b border-gray-200 dark:border-gray-800">
        {/* Breadcrumb */}
        <nav className="flex items-center text-sm text-gray-600 dark:text-gray-400">
          <Link to="/practice" className="hover:text-primary-600 dark:hover:text-primary-400 transition-colors">
            Practice
          </Link>
          <ChevronRight className="w-4 h-4 mx-1" />
          <Link to="/practice/lld" className="hover:text-primary-600 dark:hover:text-primary-400 transition-colors">
            LLD Practice
          </Link>
          <ChevronRight className="w-4 h-4 mx-1" />
          <span className="text-gray-900 dark:text-gray-100 font-medium truncate max-w-[180px]">
            {isScratch ? (brief.title || 'Blank session') : problem.title}
          </span>
          <ChevronRight className="w-4 h-4 mx-1 text-gray-300 dark:text-gray-600" />
          <span className="text-primary-600 dark:text-primary-400 font-medium">
            {SECTION_LABELS[currentSection]}
          </span>
        </nav>

        {/* Right side: timer (interview) + mode indicator */}
        <div className="flex items-center gap-3">
          {mode === 'interview' && <TimerComponent />}
          {/* Save scratch session as a reusable custom problem */}
          {isScratch && (
            <button
              onClick={handleSaveAsCustom}
              disabled={savingCustom}
              title="Save this blank session as one of your problems"
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-lg bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-200 hover:border-primary-300 dark:hover:border-primary-700 hover:text-primary-600 dark:hover:text-primary-400 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {savingCustom ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
              Save as my problem
            </button>
          )}
          {/* Mode Badge */}
          <span className={`lld-mode-badge lld-mode-badge--${mode} inline-flex items-center gap-1.5 px-3.5 py-1.5 text-sm font-semibold rounded-lg ${mode === 'learn'
            ? 'bg-primary-50 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300 ring-1 ring-primary-200 dark:ring-primary-800'
            : mode === 'practice'
              ? 'bg-emerald-50 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300 ring-1 ring-emerald-200 dark:ring-emerald-800'
              : 'bg-rose-50 dark:bg-rose-900/30 text-rose-700 dark:text-rose-300 ring-1 ring-rose-200 dark:ring-rose-800'
            }`}>
            {mode === 'learn' && <BookOpen className="w-4 h-4" />}
            {mode === 'practice' && <Lightbulb className="w-4 h-4" />}
            {mode === 'interview' && <Timer className="w-4 h-4" />}
            {mode.charAt(0).toUpperCase() + mode.slice(1)} Mode
          </span>
        </div>
      </div>

      {/* Main Content Area */}
      <div className="lld-workspace-body flex flex-1 overflow-hidden">
        {/* Section Navigator (Sidebar) */}
        <aside className="flex-shrink-0">
          <SectionNavigator />
        </aside>

        {/* Panel Content Area */}
        <main className="lld-workspace-main flex-1 flex flex-col overflow-hidden min-w-0">
          {/* Main panel content */}
          <div className="flex-1 overflow-hidden">
            {renderSectionContent()}
          </div>

          {/* ─── Mode-aware footer action bar ─────────────────────────────────── */}
          <div className="lld-workspace-actions flex items-center justify-between gap-3 px-4 py-3 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/60">
            {/* Back (all modes) */}
            <button
              onClick={() => prevSection && goToSection(prevSection)}
              disabled={!prevSection}
              className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium rounded-lg text-gray-600 dark:text-gray-300 hover:bg-gray-200/70 dark:hover:bg-gray-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
            >
              <ChevronLeft className="w-4 h-4" />
              Back
            </button>

            {/* Right-side action — progression while steps remain, single submit at the end */}
            <div className="flex items-center gap-2">
              {nextSection ? (
                mode === 'learn' ? (
                  <button
                    onClick={handleCompleteAndContinue}
                    className="inline-flex items-center gap-2 px-4 py-2 text-sm font-semibold rounded-lg bg-primary-600 hover:bg-primary-700 text-white transition-colors shadow-sm"
                  >
                    Mark done &amp; continue
                    <ArrowRight className="w-4 h-4" />
                  </button>
                ) : (
                  <button
                    onClick={() => goToSection(nextSection)}
                    className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg bg-primary-600 hover:bg-primary-700 text-white transition-colors shadow-sm"
                  >
                    {mode === 'practice' ? `Next: ${SECTION_LABELS[nextSection]}` : 'Next'}
                    <ArrowRight className="w-4 h-4" />
                  </button>
                )
              ) : (
                <>
                  <button
                    onClick={() => setShowPreview(true)}
                    className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-200 hover:border-primary-300 dark:hover:border-primary-700 hover:text-primary-600 dark:hover:text-primary-400 transition-colors"
                  >
                    <Eye className="w-4 h-4" />
                    Preview &amp; Export
                  </button>
                  <button
                    onClick={handleSubmit}
                    disabled={submitting}
                    className="inline-flex items-center gap-2 px-4 py-2 text-sm font-semibold rounded-lg bg-primary-600 hover:bg-primary-700 text-white disabled:opacity-50 disabled:cursor-not-allowed transition-colors shadow-sm"
                  >
                    {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                    Complete &amp; Submit
                  </button>
                </>
              )}
            </div>
          </div>
        </main>

        {/* ─── Guidance rail (Learn / Practice) ─────────────────────────────── */}
        {mode !== 'interview' && (
          <aside className="lld-guidance-rail hidden lg:flex flex-col w-80 flex-shrink-0 border-l border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 overflow-y-auto">
            {renderGuidanceRail()}
          </aside>
        )}
      </div>

      {/* Save status indicator */}
      <div className="lld-workspace-status flex items-center justify-between px-4 py-2 bg-white dark:bg-gray-800 border-t border-gray-200 dark:border-gray-700 text-xs text-gray-600 dark:text-gray-400">
        <span>
          {state.saveStatus === 'saving' && 'Saving...'}
          {state.saveStatus === 'saved' && state.lastSavedAt && `Last saved ${new Date(state.lastSavedAt).toLocaleTimeString()}`}
          {state.saveStatus === 'error' && (
            <span className="text-red-500">Save failed — retrying...</span>
          )}
          {state.saveStatus === 'idle' && 'Ready'}
        </span>
        <span className="text-gray-500 dark:text-gray-500">
          {problem.difficulty} • {problem.category}
        </span>
      </div>

      {/* Interview Mode: Timer Expiry Modal (Task 10.1) */}
      {showExpiryModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl p-6 max-w-md mx-4 border border-gray-200 dark:border-gray-700">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-full bg-red-100 dark:bg-red-900/30 flex items-center justify-center">
                <AlertTriangle className="w-5 h-5 text-red-600 dark:text-red-400" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                  Time's Up!
                </h3>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  Your interview time has expired
                </p>
              </div>
            </div>
            <p className="text-sm text-gray-600 dark:text-gray-300 mb-6">
              Your work will be auto-submitted in 60 seconds. You can submit now to proceed to your evaluation, or continue reviewing briefly.
            </p>
            <div className="flex gap-3">
              <button
                onClick={handleExpirySubmit}
                className="flex-1 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors text-sm font-medium"
              >
                Submit Now
              </button>
              <button
                onClick={handleExpiryContinue}
                className="flex-1 px-4 py-2 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors text-sm font-medium"
              >
                Continue (auto-submit in 60s)
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ─── Outer Container (handles data fetching + wraps with Provider) ──────────────

const ProblemWorkspace: React.FC = () => {
  const { problemId, mode } = useParams<{ problemId: string; mode: string }>();
  const navigate = useNavigate();

  const [problem, setProblem] = useState<LLDProblem | null>(null);
  const [savedProgress, setSavedProgress] = useState<LLDProgressData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Validate mode parameter
  const validMode: Mode | null = (['learn', 'practice', 'interview'] as Mode[]).includes(mode as Mode)
    ? (mode as Mode)
    : null;

  const isScratch = problemId === SCRATCH_PROBLEM_ID;

  useEffect(() => {
    if (!problemId || !validMode) {
      setError('Invalid problem or mode.');
      setLoading(false);
      return;
    }

    // Scratch / blank session: no backend record — render an in-memory blank problem.
    if (isScratch) {
      setProblem(buildScratchProblem());
      setSavedProgress(null);
      setError(null);
      setLoading(false);
      return;
    }

    const loadWorkspace = async () => {
      setLoading(true);
      setError(null);

      try {
        // Fetch problem data and saved progress in parallel
        const [problemData, progressData] = await Promise.all([
          lldProblemService.getProblemById(problemId),
          lldProgressService.getProgress(problemId, validMode).catch(() => null),
        ]);

        setProblem(problemData);
        setSavedProgress(progressData);
      } catch (err: any) {
        console.error('Failed to load workspace:', err);
        setError(err.message || 'Failed to load the workspace. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    loadWorkspace();
  }, [problemId, validMode, isScratch]);

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 dark:bg-gray-900 pt-16">
        <div className="flex flex-col items-center justify-center py-24">
          <Loader2 className="w-10 h-10 text-primary-500 animate-spin mb-4" />
          <p className="text-gray-600 dark:text-gray-400">Loading workspace...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error || !problem || !validMode) {
    return (
      <div className="min-h-screen bg-gray-100 dark:bg-gray-900 pt-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="flex flex-col items-center justify-center py-24">
            <AlertCircle className="w-10 h-10 text-red-500 mb-4" />
            <p className="text-red-600 dark:text-red-400 mb-4">
              {error || 'Could not load the workspace.'}
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => window.location.reload()}
                className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
              >
                Retry
              </button>
              <button
                onClick={() => navigate('/practice/lld')}
                className="px-4 py-2 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors"
              >
                Back to Catalog
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <LLDPracticeProvider problemId={problemId}>
      <WorkspaceInner
        problem={problem}
        mode={validMode}
        savedProgress={savedProgress}
      />
    </LLDPracticeProvider>
  );
};

export default ProblemWorkspace;
